<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { DateTime } from 'luxon';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useDestinationStore } from '@/store/DestinationStore';
import { useDurationStore } from '@/store/DurationStore';
import { useHotelStore } from '@/store/HotelStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { TabMixins } from '../mixin';
import { useTabComposable } from '../useTabComposable';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { FilterMatchMode, FilterService } from '@primevue/core/api';
import AutoComplete from 'primevue/autocomplete';
import DatePicker from 'primevue/datepicker';
import Fluid from 'primevue/fluid';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import InputTextAera from 'primevue/textarea';
import HotelRoomSelection from '@/components/tabs/HotelQuoteForm/HotelRoomSelection.vue';

type DateRange = Date | string | undefined;
interface Props {
  instanceForm?: any;
  advancedForm: any;
  calendarData?: object;
  hotels?: any;
  validate: any;
  minDateRangeValue: DateRange;
  maxDateRangeValue: any;
  isShowError?: boolean;
}

const props = defineProps<Props>();

const emits = defineEmits<{
  addOptionHotel: [];
  removeOptionHotel: [];
  addRoom: [indexOption: number, indexHotel: number];
  removeRoom: [indexOption: number, indexHotel: number];
  addHotel: [indexOption: number];
  removeHotel: [indexOption: number];
  updateMaxDayRange: [value: Date | string | undefined, optionIndex: number];
  'update:hotels': [value: any];
}>();

const hotelsData = computed({
  get() {
    return props.hotels;
  },
  set(value) {
    emits('update:hotels', value);
  }
});

const { hotelRoomPayload, handleRedirectFlight, handleSetLimit } = useTabComposable();
const adminSetupStore = useAdminSetupStore();
const currentLanguage = getCurrentLocale();
const destinationStore = useDestinationStore();
const durationStore = useDurationStore();
const hotelStore = useHotelStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const isAdvancedquoteForm = ref(true);
const hotelOptionConfigValue = ref(1);
const hotelListOptionConfigValue = ref(1);
const roomOptionConfigValue = ref(1);
const filterDestination = ref<any>(destinationStore.selectDestinationsOnlyHotel);
const viewQuoteStore = useViewQuoteStore();

const destinationsOnlyHotel = computed(() => {
  return destinationStore.selectDestinationsOnlyHotel;
});
const isRequote = computed(() => viewQuoteStore.selectIsRequote);
const isWorksheetRequote = computed(() => viewQuoteStore.isWorksheetRequote);
const hotelOptionConfig = computed(() => adminSetupStore.optionConfig?.hotelOptionConfig);

const hotelChangeRequote = (e: any, index: number) => {
  if (!e.hotels[0]?.hotel?.hCode || !isRequote.value) {
    return;
  }
  if (hotelsData.value[index].dateRange.length && hotelsData.value[index].dateRange[1] && hotelsData.value[index].adults) {
    const payload = {
      dateDep: formatDate(hotelsData.value[index].dateRange[0], 'yyyyLLdd'),
      dateRet: formatDate(hotelsData.value[index].dateRange[1], 'yyyyLLdd'),
      duration: hotelsData.value[index].duration,
      destDep: hotelsData.value[index].destination.airportCode,
      productCode: e.hotels[0].hotel.hCode,
      language: currentLanguage.toUpperCase(),
      nbAdult: e.adults,
      additionalNote: e.additionalNote,
      ...hotelRoomPayload
    };
    hotelStore.updateRoomLoading(true);
    hotelStore.getHotelRoomCategory(payload, e.hotels[0]?.hotel?.excludeHotelRoom).then(() => {
      hotelStore.updateRoomLoading(false);
    });
  }
};

const changeAdult = (option: any, index: number) => {
  hotelChangeRequote(option, index);
};

const addOptionHotel = () => {
  emits('addOptionHotel');
};

const removeOptionHotel = () => {
  emits('removeOptionHotel');
};

const addRoom = (indexOption: number, indexHotel: number) => {
  emits('addRoom', indexOption, indexHotel);
};

const removeRoom = (indexOption: number, indexHotel: number) => {
  emits('removeRoom', indexOption, indexHotel);
};

const addHotel = (indexOption: number) => {
  emits('addHotel', indexOption);
};

const removeHotel = (indexOption: number) => {
  emits('removeHotel', indexOption);
};

const selectDestination = (destination, index) => {
  clearHotel(hotelsData.value[index]);
  durationStore.updateDurationsMultiple({ durations: [], index });
  hotelStore.updateHotelMultiple({ hotels: [], index });
  hotelsData.value[index].brochure = destination?.brochure;
  const durationList = destination?.durations;
  if (durationList?.length > 0) {
    const durations = durationList.map((item) => {
      return { name: item };
    });
    durationStore.updateDurationsMultipleOnlyHotel({ durations, index });
  }
  if (destination?.code && hotelsData.value[index]?.duration) {
    getHotelListByDestination(destination?.code, index, hotelsData.value[index]);
  }
};

const getHotelListByDestination = async (destinationCode: any, hotelIndex: any, hotel: any) => {
  const payload = {
    quoteType: 'HOTEL',
    departDate: hotel?.dateRange?.[0],
    duration: hotel?.duration
  };
  if (!destinationCode) {
    return;
  }

  const hotelIds = await hotelStore.getListHotelFormDestination(destinationCode);
  if (hotelIds.data?.length) {
    await hotelStore.getHotelMultipleOnlyHotel({ hotelDurations: hotelIds.data, index: hotelIndex, data: payload });
  }
  return hotelIds;
};

const searchDestination = (event) => {
  const query = event.query;
  const newFilteredDestination = [];
  for (const destination of destinationsOnlyHotel.value) {
    const filteredItems = FilterService.filter(destination.items, ['name', 'nameFr', 'airportCode', 'country', 'countryFr'], query, FilterMatchMode.CONTAINS);
    if (filteredItems?.length) {
      newFilteredDestination.push({ ...destination, ...{ items: filteredItems } });
    }
  }
  filterDestination.value = TabMixins.handleDuplicate(newFilteredDestination);
};

const changeDate = (event: any, index: number, option: any) => {
  clearHotel(hotelsData.value[index]);

  hotelsData.value[index].isReturn = !hotelsData.value[index].dateRange[1];

  const addDate = new Date(DateTime.fromJSDate(hotelsData.value[index].dateRange[0]).plus({ days: 14 }));
  getMaxDate(addDate, index, hotelsData.value[index].dateRange);
  let diffDays = DateTime.fromJSDate(new Date(hotelsData.value[index].dateRange[1]))
    .diff(DateTime.fromJSDate(new Date(hotelsData.value[index].dateRange[0])), 'days')
    .toObject().days;
  diffDays = Math.ceil(diffDays);
  if (diffDays < 0) {
    hotelsData.value[index].duration = 0;
    return;
  }
  if (isNaN(diffDays) || diffDays === 0) {
    hotelsData.value[index].duration = 1;
  } else {
    hotelsData.value[index].duration = diffDays;
  }
  hotelChangeRequote(option, index);
  if (option?.destination?.code && option?.duration) {
    getHotelListByDestination(option?.destination?.code, index, option);
  }
  hotelStore.clearHotelRoomCategory();
};

const getMaxDate = (date: any, index: number, dateSelected: any) => {
  if (!props.maxDateRangeValue) {
    emits('updateMaxDayRange', new Date(date), index);
  } else {
    emits('updateMaxDayRange', dateSelected[1] ? undefined : new Date(date), index);
  }
};

const clearDestination = (index: number) => {
  hotelsData.value[index].destination = '';
  clearHotel(hotelsData.value[index]);
};

const clearHotel = (option: any) => {
  if (option.hotels.length) {
    option.hotels.forEach((hotel: any) => {
      hotel.hotel = '';
      hotel.rooms.forEach((room: any) => {
        room.room = '';
      });
    });
  }
};

const handleConfigReplicate = () => {
  if (hotelsData.value.length > hotelOptionConfigValue.value) {
    hotelOptionConfigValue.value = hotelsData.value.length;
  }
  hotelsData.value.forEach((option: any) => {
    if (option.hotels.length > hotelListOptionConfigValue.value) {
      hotelListOptionConfigValue.value = option.hotels.length;
    }

    option.hotels.forEach((hotel: any) => {
      if (hotel.rooms.length > roomOptionConfigValue.value) {
        roomOptionConfigValue.value = hotel.rooms.length;
      }
    });
  });
};

const clearHotelOptions = (maxOption: number, maxHotel: number, maxRoom: number) => {
  if (isRequote.value && isWorksheetRequote.value) {
    handleConfigReplicate();
    return;
  }
  if (isRequote.value && hotelsData.value.length && hotelsData.value.length > maxOption) {
    hotelsData.value.splice(maxOption);
  }
  hotelsData.value.forEach((item: any) => {
    if (isRequote.value && item.hotels.length > maxHotel) {
      item.hotels.splice(maxHotel);
    }

    item.hotels.forEach((hotel: any) => {
      if (isRequote.value && hotel.rooms.length > maxRoom) {
        hotel.rooms.splice(maxRoom);
      }
    });
  });
};

const handleChangeGroupTypeHotel = (advancedForm: any, config: any) => {
  if (advancedForm[0].groupType) {
    hotelOptionConfigValue.value = handleSetLimit(advancedForm, config).maxOption;
    hotelListOptionConfigValue.value = handleSetLimit(advancedForm, config).maxHotel;
    roomOptionConfigValue.value = handleSetLimit(advancedForm, config).maxRoom;
    clearHotelOptions(handleSetLimit(advancedForm, config).maxOption, handleSetLimit(advancedForm, config).maxHotel, handleSetLimit(advancedForm, config).maxRoom);
  }
};

watch(
  () => props.advancedForm,
  (newValue) => {
    if (newValue) {
      handleChangeGroupTypeHotel(newValue, hotelOptionConfig.value);
    }
  },
  { deep: true, immediate: true }
);
</script>
<template>
  <div class="hotel-form-input">
    <div v-for="(option, index) in hotelsData" :key="index">
      <Fluid>
        <p v-if="isAdvancedquoteForm">{{ $t('common.option') }} {{ index + 1 }}</p>
        <p @click="handleRedirectFlight()" class="cursor-pointer flex gap-2 align-items-center justify-content-end"><i class="text-3xl pi pi-exclamation-circle exclamation-icon"></i> {{ $t('common.where_we_fly') }}</p>
        <div class="field">
          <label for="dest" class="long-text" v-tooltip.top="$t('advance_quote.form.destination')">{{ $t('advance_quote.form.destination') }}</label>
          <div class="flex align-items-center option-destination-class">
            <AutoComplete
              id="destination-selection"
              v-model="option.destination"
              :suggestions="filterDestination"
              @complete="searchDestination($event)"
              :optionLabel="getTextVariant(currentLanguage, 'name', 'nameFr')"
              optionGroupLabel="label"
              optionGroupChildren="items"
              :completeOnFocus="true"
              optionValue="name"
              @change="selectDestination(option.destination, index)"
              :class="isShowError && validate[index].destination[0]?.$message ? 'p-invalid' : ''"
              class="w-full"
              forceSelection
            >
              <template #option="slotProps">
                <div class="ml-7">{{ getTextVariant(currentLanguage, `${slotProps.option.name} (${slotProps.option.country})`, `${slotProps.option.nameFr} (${slotProps.option.countryFr})`) }}</div>
              </template>
            </AutoComplete>
            <div>
              <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearDestination(index)"></i>
            </div>
          </div>
          <span class="text-red-500" v-if="isShowError">
            {{ validate[index].destination[0]?.$message }}
          </span>
        </div>
      </Fluid>
      <div class="grid">
        <div class="col-12 lg:col-4 md:col-6">
          <Fluid>
            <div class="field option-date-range-class">
              <label for="dest" class="head-size" v-tooltip.top="$t('advance_quote.form.date_range')">{{ $t('advance_quote.form.date_range') }}</label>
              <DatePicker
                v-model="option.dateRange"
                selectionMode="range"
                :minDate="minDateRangeValue"
                dateFormat="dd-M-yy"
                :maxDate="maxDateRangeValue[index]"
                @update:modelValue="changeDate($event, index, option)"
                :class="isShowError && validate[index].dateRange[0]?.$message ? 'p-invalid' : ''"
                :selectOtherMonths="true"
                :placeholder="$t('common.date_format_range')"
                class="placeholder-size"
                :disabled="!advancedForm[0].groupTypeId"
              >
              </DatePicker>
              <span class="text-red-500" v-if="isShowError">
                {{ validate[index].dateRange[0]?.$message }}
              </span>
              <br />
              <span class="text-red-500" v-if="option.isReturn"> {{ $t('create_quote.required_return_date') }} </span>
            </div>
          </Fluid>
        </div>
        <div class="col-12 lg:col-2 md:col-6">
          <Fluid>
            <div class="field">
              <label for="duration" class="head-size" v-tooltip.top="$t('advance_quote.form.duration')">{{ $t('advance_quote.form.duration') }}</label>
              <InputText id="duration" v-model="option.duration" :disabled="true" :placeholder="$t('advance_quote.form.duration')" />
            </div>
          </Fluid>
        </div>
        <div class="col-12 lg:col-2 md:col-4">
          <Fluid>
            <div class="field option-num-of-rooms-class">
              <label for="number_room" class="head-size long-text" v-tooltip.top="$t('advance_quote.form.number_of_room')">{{ $t('advance_quote.form.nb_of_room') }}</label>
              <InputNumber :min="0" id="number_room" :class="isShowError && validate[index].numOfRooms[0]?.$message ? 'p-invalid' : ''" type="text" v-model="option.numOfRooms" />
              <span class="text-red-500" v-if="isShowError">
                {{ validate[index].numOfRooms[0]?.$message }}
              </span>
            </div>
          </Fluid>
        </div>
        <div class="col-12 lg:col-2 md:col-4">
          <Fluid>
            <div class="field option-adults-class">
              <label for="adt" class="head-size" v-tooltip.top="$t('advance_quote.form.adult')">{{ $t('advance_quote.form.adult') }}</label>
              <InputNumber :min="0" id="adt" type="text" v-model="option.adults" :class="isShowError && validate[index].adults[0]?.$message ? 'p-invalid' : ''" @update:modelValue="changeAdult(option, index)" />
              <span class="text-red-500" v-if="isShowError">
                {{ validate[index].adults[0]?.$message }}
              </span>
            </div>
          </Fluid>
        </div>
        <div class="col-12 lg:col-2 md:col-4">
          <Fluid>
            <div class="field option-children-class">
              <label for="chd" class="head-size long-text" v-tooltip.top="$t('advance_quote.form.children')">{{ $t('advance_quote.form.children_short') }}</label>
              <InputNumber :min="0" id="chd" type="text" v-model="option.children" />
            </div>
          </Fluid>
        </div>
      </div>
      <div class="hotel-select">
        <HotelRoomSelection
          :instanceForm="instanceForm"
          :advancedForm="advancedForm"
          :calendarData="calendarData"
          v-model:hotels="hotelsData"
          :validate="validate[index]"
          :minDateRangeValue="minDateRangeValue"
          :maxDateRangeValue="maxDateRangeValue[index]"
          :hotelOptionConfig="hotelListOptionConfigValue"
          :roomOptionConfig="roomOptionConfigValue"
          :hotelOption="option"
          :indexOption="index"
          :isShowError="isShowError"
          @addRoom="(indexOption, indexHotel) => addRoom(indexOption, indexHotel)"
          @removeRoom="(indexOption, indexHotel) => removeRoom(indexOption, indexHotel)"
          @addHotel="(indexOption) => addHotel(indexOption)"
          @removeHotel="(indexOption) => removeHotel(indexOption)"
        />
      </div>
      <Fluid v-if="authorizations?.advance_quote?.additional_note_view">
        <div class="field additional-note-class flex-col">
          <label for="additionalNote">{{ $t('advance_quote.form.additional_note') }}</label>
          <InputTextAera v-model="option.additionalNote" class="not-resize" :autoResize="true" rows="5" cols="30" maxlength="500" />
        </div>
      </Fluid>
    </div>
    <div class="grid" v-if="isAdvancedquoteForm">
      <div class="flex w-full justify-content-around mb-3">
        <div id="remove-option-btn" class="block text-primary cursor-pointer" @click="removeOptionHotel()" v-if="hotelsData.length > 1">- {{ $t('advance_quote.form.remove_option') }}</div>
        <div id="add-option-btn" class="block text-primary cursor-pointer" v-if="hotelsData.length < hotelOptionConfigValue" @click="addOptionHotel()">+ {{ $t('advance_quote.form.add_option') }}</div>
      </div>
    </div>
  </div>
</template>
